/**
 * @author Gananath Athukorala
 * @version 0.1
 */

import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import api from "../../services/AuthApi";
import { JSEncrypt } from "jsencrypt";
import base64url from "base64url";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Switch,
  TextField,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { RequestErrorHandeling } from "../../errorHandeling/RequestErrorHandeling";
import Alerts from "../../errorHandeling/Alerts";

const encrypt = new JSEncrypt();
const publicKey = `
    -----BEGIN PUBLIC KEY-----
    MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAsy+1EQqpdOCQM4OaAflU9rQZGzo8TexVbkez7q67egtwZT5BCG5bClBk95paacF3HgMwh4se6ZhttdkiJfr/gGagOqP2FTZKqEaz7d24FSpYyKUVdgZ7eDNgiBbkWVZi83dGqlMGM2wmES3Q614P3JN+BiZ5srvc4q0BXnw8y40MB10KY0dONVuKrywKEKoHCfjIvKJNH3FyYaCwslQC4m94ErDr5OGbO3qPLiJzw7fRoU3WN05OZvGuYkgS0yN2KUKOp/vAyG6h1Mf+bayILTAGJRxyYdLGvL8Y/90NIFNvXtO4IHfY8HO/bymOic/gPE7XU1lHTK1EvrF55a/E3wIDAQAB
    -----END PUBLIC KEY-----`;
// Assign encryptor to utilize the public key.
encrypt.setPublicKey(publicKey);

const GridWrapper = styled(Grid)(({ theme }) => ({
  alignItems: "center",
}));

const ResetPasswordModal = ({ username, open, onClose }) => {
  const [passwordJSON, setPasswordJSON] = useState({
    firstLogin: false,
  });
  const [isSubmit, setIsSubmit] = useState(false);
  const [isPasswordError, setIsPasswordError] = useState(false);
  const [passwordErrorMessages, setPasswordErrorMessages] = useState([]);

  //for success messages
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessages, setSuccessMessages] = useState([]);

  //for error handling
  const [iserror, setIserror] = useState(false);
  const [iserrorShow, setIserrorShow] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setIsPasswordError(false);
    setPasswordJSON({
      ...passwordJSON,
      [name]: value,
    });
  };

  const handleIsFirstLogin = (e) => {
    setPasswordJSON((prevState) => ({
      ...prevState,
      firstLogin: e.target.checked,
    }));
  };

  // Edit
  let putConfig = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  const handleSaveRowEdits = (e) => {
    e.preventDefault();
    const inputNewPassword = passwordJSON.newPassword;
    const confirmPassword = passwordJSON.confirmPassword;
    const reg = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)(?=.*?[#?!@$%^&*-]).{8,}$/;
    // Perform our encryption based on our public key - only private key can read it!
    const encryptedNewPassword = encrypt.encrypt(inputNewPassword);
    const encryptedConfirmPassword = encrypt.encrypt(confirmPassword);
    //Converting the encrypted password to url friendly base64URL encoded
    const urlEncodedNewPassword = base64url.toBase64(encryptedNewPassword);
    const urlEncodedConfirmPassword = base64url.toBase64(
      encryptedConfirmPassword
    );
    if (inputNewPassword === confirmPassword) {
      if (reg.test(inputNewPassword)) {
        setIsSubmit(true);
        const copyData = {};
        copyData.username = username;
        copyData.newPassword = urlEncodedNewPassword;
        copyData.confirmPassword = urlEncodedConfirmPassword;
        copyData.firstLogin = passwordJSON.firstLogin;
        api
          .put("/users/reset-password", copyData, putConfig)
          .then((res) => {
            setErrorMessages([]);
            setPasswordErrorMessages([]);
            setIserror(false);
            setIsSuccess(true);
            setSuccessMessages([res.data?.description]);
            setTimeout(() => {
              handleClearData();
              setIsPasswordError(false);
            }, 3000);
          })
          .catch((error) => {
            setPasswordErrorMessages([]);
            setIsSuccess(false);
            RequestErrorHandeling(
              error,
              setIserror,
              setErrorMessages,
              setIserrorShow
            );
            setIsPasswordError(false);
            setIsSubmit(false);
          });
      } else {
        setPasswordErrorMessages([
          "Password should be minimum eight characters, at least one upper case letter, one lower case letter, one number and one special character",
        ]);
        setIserror(true);
      }
    } else {
      setPasswordErrorMessages(["Passwords don't match, Try again"]);
      setIsPasswordError(true);
    }
  };

  const handleClearData = () => {
    onClose();
    setPasswordJSON({
      firstLogin: false,
    });
    setPasswordErrorMessages([]);
    setIsPasswordError(false);
    setIsSubmit(false);
    setIsSuccess(false);
    setSuccessMessages([""]);
    setIserror(false);
    setIserrorShow(false);
    setErrorMessages([]);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      PaperProps={{ style: { borderRadius: "10px" } }}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#116c6f",
          color: "white",
        }}>
        Reset Password
        <IconButton
          aria-label="cancel"
          sx={{ color: "#fff" }}
          onClick={handleClearData}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSaveRowEdits}>
        <DialogContent dividers>
          <div>
            <Alerts
              iserror={iserror}
              errorMessages={errorMessages}
              isSuccess={isSuccess}
              successMessages={successMessages}
              iserrorShow={iserrorShow}
            />
          </div>
          <Box display="flex" justifyContent="center" alignItems="center">
            <GridWrapper container spacing={2} width="100%">
              <Grid item xs={12} mt={2}>
                <TextField
                  fullWidth
                  disabled
                  id="inputUserName"
                  variant="outlined"
                  name="username"
                  value={username ? username : ""}
                  label="User Name"
                  required
                  onChange={handleOnChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="newPassword"
                  label="New Password"
                  type="password"
                  value={
                    passwordJSON.newPassword ? passwordJSON.newPassword : ""
                  }
                  required
                  onChange={handleOnChange}
                  error={isPasswordError}
                  helperText={passwordErrorMessages}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  value={
                    passwordJSON.confirmPassword
                      ? passwordJSON.confirmPassword
                      : ""
                  }
                  required
                  onChange={handleOnChange}
                  error={isPasswordError}
                  helperText={passwordErrorMessages}
                />
              </Grid>
              <Grid item xs={12}>
                <h6>Is First Logged In </h6>
                <Switch
                  name="firstLogin"
                  checked={passwordJSON.firstLogin}
                  onChange={handleIsFirstLogin}
                />
              </Grid>
            </GridWrapper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: "1.25rem" }}>
          <Button onClick={handleClearData} variant="outlined" color="error">
            Cancel
          </Button>
          <LoadingButton
            loading={isSubmit}
            loadingPosition="start"
            startIcon={<SaveIcon />}
            variant="contained"
            color="success"
            type="submit">
            Reset
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ResetPasswordModal;
